/**
 * Functions that return or update timekeeping model or app data only.
 */

import { getAPIData, apiRequest } from "./axios";
import { API_PATHS } from "../../config/apiConfig";

/**
 * Count of payout timesheets still awaiting review.
 *
 * @returns {Promise<Object|number>}
 */
export async function getPendingPayoutTimesheetsCount() {
  const url = API_PATHS.TIMEKEEPING.PENDING_PAYOUT_COUNT;
  return await getAPIData(url);
}

/**
 * Detail rows for payout timesheets still awaiting review.
 *
 * @returns {Promise<Array|Object>}
 */
export async function getPendingPayoutTimesheets() {
  const url = `/timekeeping/getpendingpayouttimesheetsdetails`;
  return await getAPIData(url);
}

/**
 * Payout timesheet dashboard rows with optional query filters.
 *
 * @param {Object} [params={}] Query params forwarded to the dashboard endpoint.
 * @returns {Promise<Object>}
 */
export async function getPayoutTimesheetDashboard(params = {}) {
  const query = new URLSearchParams(params).toString();
  const url = `${API_PATHS.TIMEKEEPING.PAYOUT_DASHBOARD}${query ? `?${query}` : ""}`;
  return await getAPIData(url);
}

/**
 * Compact worker-shift rows for one payroll week's summary CSV.
 *
 * @param {string} weekEnding Week-ending date (YYYY-MM-DD).
 * @returns {Promise<Object>} Week starting/ending dates and per-worker shifts.
 */
export async function getPayoutWeekSummary(weekEnding) {
  const url = API_PATHS.TIMEKEEPING.PAYOUT_WEEK_SUMMARY(weekEnding);
  return await getAPIData(url);
}

/**
 * Fetch full project payout-hour entries (filter Type/Range on the client).
 *
 * @param {Object} params
 * @param {number|string} params.job - JobNumber primary key (required).
 * @param {Object} [config] - Optional axios config (e.g. ``signal`` for abort).
 * @returns {Promise<Object>} ``{ job, entries, workerTypes }``.
 */
export async function getProjectPayoutHours({ job } = {}, config = {}) {
  const params = new URLSearchParams();
  if (job != null) {
    params.set("job", String(job));
  }
  const query = params.toString();
  const url = `${API_PATHS.TIMEKEEPING.PROJECT_PAYOUT_HOURS}${query ? `?${query}` : ""}`;
  return await apiRequest(url, null, { method: "GET", ...config });
}

/**
 * Fetch hours for the most recently IIF-exported payroll week.
 *
 * Resolves the week from NotificationRecord on the backend. Optional ``job``
 * scopes totals to one project; omit for all jobs.
 *
 * @param {Object} [params]
 * @param {number|string} [params.job] - JobNumber primary key.
 * @param {Object} [config] - Optional axios config (e.g. ``signal`` for abort).
 * @returns {Promise<{weekEnding: string|null, weekStarting: string|null, job: number|null, totalHours: number}>}
 */
export async function getLatestPayrollWeekData({ job } = {}, config = {}) {
  const params = new URLSearchParams();
  if (job != null) {
    params.set("job", String(job));
  }
  const query = params.toString();
  const url = `${API_PATHS.TIMEKEEPING.LATEST_PAYROLL_WEEK_DATA}${query ? `?${query}` : ""}`;
  return await apiRequest(url, null, { method: "GET", ...config });
}

/**
 * Dispatch-backed schedule for one worker on a date.
 *
 * @param {number|string} userId CustomUser PK.
 * @param {string} date YYYY-MM-DD.
 * @returns {Promise<Object>}
 */
export async function getUserSchedule(userId, date) {
  const url = `/timekeeping/getuserschedule/${userId}/${date}`;
  return await getAPIData(url);
}

/**
 * Current on-shift Event (if any) for the session user on `date`.
 *
 * @param {string} date YYYY-MM-DD.
 * @returns {Promise<Object>}
 */
export async function getUserOnShift(date) {
  const url = `/timekeeping/getuseronshift/${date}`;
  return await getAPIData(url);
}

/**
 * Completed DailyTimeSheet for the session user on `date`.
 *
 * @param {string} date YYYY-MM-DD.
 * @returns {Promise<Object>}
 */
export async function getDailyTimesheet(date) {
  const url = `/timekeeping/getdailytimesheet/${date}`;
  return await getAPIData(url);
}

/**
 * Sunday-week of shift rows for a user, or the session user when `userId` is omitted.
 *
 * @param {string} date Any date in the target week (YYYY-MM-DD).
 * @param {number|string|null} [userId=null] Optional worker PK.
 * @returns {Promise<Object>}
 */
export async function getWeekOfShifts(date, userId = null) {
  let url = `/timekeeping/getweekofshifts/${date}`;
  if (userId) {
    url += `?user_id=${userId}`;
  }
  return await getAPIData(url);
}

/**
 * Fetch a week of manager hours-log entries for one worker.
 *
 * @param {number|string} userID - Worker user id.
 * @param {string} weekEnding - Week ending date (YYYY-MM-DD).
 * @returns {Promise<Object>} Manager hours-log week payload.
 */
export async function getWeekOfManagerHoursLogs(userID, weekEnding) {
  const url = `/timekeeping/getweekofhourslogs/${userID}/${weekEnding}`;
  return await getAPIData(url);
}

/**
 * Payout-review timesheet payload for one worker and week ending.
 *
 * @param {number|string} userID Worker PK.
 * @param {string} weekEnding YYYY-MM-DD week ending.
 * @returns {Promise<Object>}
 */
export async function getPayoutTimesheetData(userID, weekEnding) {
  const url = `/timekeeping/getpayouttimesheetdata/${userID}/${weekEnding}`;
  return await getAPIData(url);
}

/**
 * Whether the worker timesheet signing prompt should be shown.
 *
 * @returns {Promise<Object>}
 */
export async function getShowTimesheetSigningPrompt() {
  const url = `/timekeeping/getshowtimesheetsigningprompt/`;
  return await getAPIData(url);
}

/**
 * Week-ending date the worker timesheet UI should open on.
 *
 * @returns {Promise<Object>}
 */
export async function getWorkerTimesheetWeekEnding() {
  const url = `/timekeeping/getworkertimesheetweekending/`;
  return await getAPIData(url);
}

/**
 * Stored worker timesheet signature for a user and week ending.
 *
 * @param {number|string} userID Worker PK.
 * @param {string} weekEnding YYYY-MM-DD week ending.
 * @returns {Promise<Object>}
 */
export async function getWorkerTimesheetSignature(userID, weekEnding) {
  const url = `/timekeeping/getworkertimesheetsignature/${userID}/${weekEnding}`;
  return await getAPIData(url);
}

/**
 * Whether a PDF/weekly timesheet already exists for `weekEnding`.
 *
 * @param {string} weekEnding YYYY-MM-DD week ending.
 * @returns {Promise<Object>}
 */
export async function checkTimesheetExists(weekEnding) {
  const url = `/timekeeping/checktimesheetexists/${weekEnding}`;
  return await getAPIData(url);
}

/**
 * Clock a worker in (creates an Event).
 *
 * @param {Object} payload Clock-in fields accepted by the backend.
 * @returns {Promise<Object>}
 */
export async function clockInUser(payload) {
  const url = "/timekeeping/clockinuser";
  return await apiRequest(url, payload, { method: "POST" });
}

/**
 * Cancel an in-progress clock-in Event.
 *
 * @param {Object} payload Cancel payload (event/user identifiers).
 * @returns {Promise<Object>}
 */
export async function cancelClockIn(payload) {
  const url = "/timekeeping/cancelclockin";
  return await apiRequest(url, payload, { method: "POST" });
}

/**
 * Clock a worker out (completes the Event into a DailyTimeSheet).
 *
 * @param {Object} payload Clock-out fields accepted by the backend.
 * @returns {Promise<Object>}
 */
export async function clockOutUser(payload) {
  const url = "/timekeeping/clockoutuser";
  return await apiRequest(url, payload, { method: "POST" });
}

/**
 * Edit an existing completed shift via `/timekeeping/editshift`.
 *
 * @param {Object} payload Shift edit fields.
 * @returns {Promise<Object>}
 */
export async function editShiftData(payload) {
  const url = "/timekeeping/editshift";
  return await apiRequest(url, payload, { method: "POST" });
}

/**
 * Create a completed shift via `/timekeeping/savecompleteshift` (no clock-in Event).
 *
 * @param {Object} payload Full shift fields including `date` as YYYY-MM-DD.
 * @returns {Promise<Object>}
 */
export async function saveShiftData(payload) {
  const url = "/timekeeping/savecompleteshift";
  return await apiRequest(url, payload, { method: "POST" });
}

/**
 * Save a worker-signed weekly timesheet.
 *
 * @param {Object} payload Signature and week payload.
 * @returns {Promise<Object>}
 */
export async function saveWorkerTimesheet(payload) {
  const url = "/timekeeping/saveworkertimesheet";
  return await apiRequest(url, payload, { method: "POST" });
}

/**
 * Save payout-review hours/job (legacy `manager_*` field names on the backend).
 *
 * @param {Object} payload Payout timesheet payload.
 * @returns {Promise<Object>}
 */
export async function savePayoutTimesheet(payload) {
  const url = "/timekeeping/savepayouttimesheet";
  return await apiRequest(url, payload, { method: "POST" });
}
